import React from "react";
import ConfirmHeader from "../../components/ConfirmHeader";
import { ReminderToggleRow } from "../../components/ReminderToggleRow";
import { setItemRemindersOn } from "../../utils/itemReminderToggle";
import { Capacitor } from "@capacitor/core";
import {
  X, Bell, Trash2, Tag, CalendarDays, CheckSquare,
  CheckCircle, Clock, XCircle, Flag, Plus, ChevronDown,
} from "lucide-react";
import { MapPin } from "../../PhosphorIcons";
import { S as _S, DETAIL_BACKDROP, CONFIRM_STACK, CONFIRM_DANGER, CONFIRM_CANCEL } from "../../theme/sharedStyles";
import { categoryInk } from "../../theme/colorEngine";
import { PRIORITY_COLORS, TASK_PRIORITIES, DEFAULT_TASK_COLOR, DEFAULT_EVENT_COLOR } from "../../constants/palettes";
import { DEFAULT_ALARM_SOUND } from "../../constants/misc";
import { uid, load } from "../../utils/storage";
import { quickDateLabel, fmtQuickTime } from "../../utils/quickAdd";
import { MonthPicker, TimeRow, EndTimeRow, AllDayRow } from "./DatePickParts";
import { hasPickedDueDate } from "../../utils/taskDueLabel";
import { durationLabel } from "../../utils/reminderFormat";
import { CategoryIcon } from "../../components/CategoryIcon";
import { NativePickerInput } from "../../components/NativePickerInput";
import { InlineCategoryCreator } from "../../components/InlineCategoryCreator";
import { AttachmentViewer } from "../../components/attachments/AttachmentViewer";
import { AttachmentField } from "../../components/attachments/AttachmentField";
import { LocationAutocomplete } from "../../components/pickers/LocationAutocomplete";
import { ReminderPicker } from "../../components/pickers/ReminderPicker";
import { ModalColorPicker } from "../../components/pickers/ModalColorPicker";
import { RichNotesEditor } from "../../components/RichNotesEditor";
import { HabitCheckbox } from "../../components/HabitCheckbox";
import { EmbedPill } from "../../components/EmbedPill";
import { isValidHttpUrl } from "../../utils/linkEmbed";
import { buildItemPayload } from "../../utils/itemPayload";
import { IntervalPicker } from "../../components/pickers/IntervalPicker";
import { isTaskDoneOn } from "../../utils/taskLogic";
import { ItemActionButtons } from "./ItemActionButtons";
import { MarkUpcomingPanel } from "./MarkUpcomingPanel";
import { isEventPassed, eventStatusTap, applyKeepUpcoming, applyMoveEvent } from "../../utils/eventStatus";
import { isGoogleTaskEvent, googleTaskUrl } from "../../google/googleTask";
import { GoogleTaskLink, GoogleTaskDeleteNotice } from "./GoogleTaskLink";

// Yearly-frequency date picker helpers (0-based month index).
const MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]; // Feb=29 so leap-day dates are pickable

export function WeekBlockPopoverPortal({ weekBlockPop, categories, eventColor, closeWeekBlockPop,
  openEditModal, openEditEvent, openEditReminder,
  deleteTaskFromCal, deleteEvent, deleteReminder, toggleTaskDoneFromCal,
  tasks, calEvents, calReminders,
  setTasks, setCalEvents, setCalReminders, savedColors, addSavedColor,
  taskOccurrenceOverrides = {}, setTaskOccurrenceOverrides = () => {},
  calEventOccurrenceOverrides = {}, setCalEventOccurrenceOverrides = () => {} }) {

  const { item, type, occDate } = weekBlockPop;
  // Use live item so done/passed state updates immediately after toggle.
  // For a recurring task/event opened from a specific occurrence, merge that
  // occurrence's overrides (time, color, and — for tasks — per-day sub-task
  // done state) so the popover (and the edit modal it opens) shows this
  // block's own state, not the shared base item's.
  const occOverride = occDate
    ? (type === "event" ? calEventOccurrenceOverrides[`${item.id}:${occDate}`] : taskOccurrenceOverrides[`${item.id}:${occDate}`])
    : null;
  // frequencyType applies to both tasks and events; the legacy `repeat` field
  // (events only) is checked as a fallback for events created via the older
  // Add/Edit Event form — see taskLogic.js's _computeOccurringOn.
  const isRecurringItem = !!((item.frequencyType && item.frequencyType !== "once") || (type !== "task" && item.repeat && item.repeat !== "None"));
  const liveItem = (() => {
    if (type === "event") {
      const base = calEvents.find(e => e.id === item.id) || item;
      return occOverride ? { ...base, time: occOverride.time ?? base.time, endTime: occOverride.endTime ?? base.endTime, color: occOverride.color || base.color } : base;
    }
    if (type === "reminder") return calReminders.find(r => r.id === item.id) || item;
    const base = tasks.find(t => t.id === item.id) || item;
    if (!occOverride) return base;
    const withOv = { ...base, time: occOverride.time ?? base.time, endTime: occOverride.endTime ?? base.endTime, color: occOverride.color || base.color };
    if (occOverride.subtaskDone && base.subtasks?.length) {
      withOv.subtasks = base.subtasks.map(s => (s.id in occOverride.subtaskDone) ? { ...s, done: occOverride.subtaskDone[s.id] } : s);
    }
    return withOv;
  })();
  // Recurring tasks track done per-occurrence — evaluate against THIS block's
  // date, not the shared item's own `done` flag (which stays for one-time tasks).
  const taskIsDone = type === "task" && isTaskDoneOn(liveItem, occDate, taskOccurrenceOverrides);
  const openedAtRef = React.useRef(Date.now());
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [gTaskNotice, setGTaskNotice] = React.useState(false); // Delete on a Google Task
  const [upcomingOpen, setUpcomingOpen] = React.useState(false); // Mark as upcoming panel (events)

  const cat = categories.find(c => c.id === (liveItem.goalId || liveItem.categoryId));

  const handleEdit = () => {
    closeWeekBlockPop();
    if (type === "task") openEditModal(liveItem, "task", occDate);
    else if (type === "event") openEditEvent(liveItem, occDate);
    else if (type === "reminder") openEditReminder(liveItem);
  };

  const handleDelete = () => {
    closeWeekBlockPop();
    if (type === "task") deleteTaskFromCal(liveItem.id);
    else if (type === "event") deleteEvent(liveItem.id);
    else if (type === "reminder") deleteReminder(liveItem.id);
  };

  const handleSkip = () => {
    closeWeekBlockPop();
    window.dispatchEvent(new CustomEvent("clarity:openSkipFlow", { detail: { item: liveItem, type, date: occDate } }));
  };

  // Toggle a sub-task's done state directly from the popover. For a recurring
  // task opened from a specific occurrence, this is stored as a per-occurrence
  // override so checking it off today doesn't check it off for every other
  // day the task recurs on — only a one-off task's subtasks are shared globally.
  const toggleSubtask = (subId) => {
    if (isRecurringItem && occDate) {
      const key = `${liveItem.id}:${occDate}`;
      const baseDone = (tasks.find(t => t.id === liveItem.id)?.subtasks || []).find(s => s.id === subId)?.done;
      const currentDone = liveItem.subtasks.find(s => s.id === subId)?.done ?? baseDone;
      setTaskOccurrenceOverrides(prev => ({
        ...prev,
        [key]: { ...prev[key], subtaskDone: { ...(prev[key]?.subtaskDone || {}), [subId]: !currentDone } },
      }));
      return;
    }
    setTasks(p => p.map(t => t.id === liveItem.id
      ? { ...t, subtasks: (t.subtasks || []).map(s => s.id === subId ? { ...s, done: !s.done } : s) }
      : t));
  };

  // Notes can contain "rn-check" checklist rows (same markup the RichNotesEditor
  // inserts) — click-to-toggle them here too. For a recurring task/event opened
  // from a specific occurrence the checked state is stored PER-OCCURRENCE (by
  // checkbox index) so ticking a note item off today leaves every other day's
  // copy untouched; only a one-off item writes the toggle back to shared notes.
  const handleNotesClick = (e) => {
    const box = e.target.closest && e.target.closest(".rn-check-box");
    if (!box) return;
    const label = box.parentElement && box.parentElement.querySelector(".rn-check-label");
    const willCheck = box.getAttribute("data-checked") !== "1";
    // Immediate visual feedback (React re-render below keeps it in sync).
    if (willCheck) {
      box.setAttribute("data-checked", "1");
      box.textContent = "✓";
      box.style.background = "#d4789a";
      if (label) { label.style.textDecoration = "line-through"; label.style.opacity = "0.55"; }
    } else {
      box.removeAttribute("data-checked");
      box.textContent = "";
      box.style.background = "transparent";
      if (label) { label.style.textDecoration = "none"; label.style.opacity = "1"; }
    }
    if (isRecurringItem && occDate && (type === "task" || type === "event")) {
      const boxes = Array.from(e.currentTarget.querySelectorAll(".rn-check-box"));
      const idx = boxes.indexOf(box);
      const key = `${liveItem.id}:${occDate}`;
      const setter = type === "event" ? setCalEventOccurrenceOverrides : setTaskOccurrenceOverrides;
      setter(prev => ({
        ...prev,
        [key]: { ...prev[key], notesChecks: { ...(prev[key]?.notesChecks || {}), [idx]: willCheck } },
      }));
      return;
    }
    const newHtml = e.currentTarget.innerHTML;
    if (type === "task") setTasks(p => p.map(t => t.id === liveItem.id ? { ...t, notes: newHtml } : t));
    else if (type === "event") setCalEvents(p => p.map(x => x.id === liveItem.id ? { ...x, notes: newHtml } : x));
    else if (type === "reminder") setCalReminders(p => p.map(x => x.id === liveItem.id ? { ...x, notes: newHtml } : x));
  };

  // Apply this occurrence's per-index note check overrides on top of the shared
  // notes HTML, so the displayed checkboxes reflect THIS day only. The notes
  // TEXT stays shared; only the tick state diverges per occurrence.
  const displayNotes = (() => {
    const base = liveItem.notes;
    const checks = occOverride?.notesChecks;
    if (!base || !checks || !Object.keys(checks).length) return base;
    const tmp = document.createElement("div");
    tmp.innerHTML = base;
    const boxes = tmp.querySelectorAll(".rn-check-box");
    boxes.forEach((box, i) => {
      if (!(i in checks)) return;
      const label = box.parentElement && box.parentElement.querySelector(".rn-check-label");
      if (checks[i]) {
        box.setAttribute("data-checked", "1"); box.textContent = "✓"; box.style.background = "#d4789a";
        if (label) { label.style.textDecoration = "line-through"; label.style.opacity = "0.55"; }
      } else {
        box.removeAttribute("data-checked"); box.textContent = ""; box.style.background = "transparent";
        if (label) { label.style.textDecoration = "none"; label.style.opacity = "1"; }
      }
    });
    return tmp.innerHTML;
  })();

  // Style helpers matching ItemDetailPanel (./ItemDetailPanel.jsx)
  const accentColor = type === "task" ? "#d4789a" : type === "event" ? (eventColor(liveItem) || "#d4789a") : "#5bbfb5";
  // A Google Task synced in from Google Calendar is stored as an event but
  // labelled a Task (see src/google/googleTask.js).
  const fromGoogleTasks = type === "event" && isGoogleTaskEvent(liveItem);
  // This occurrence's Upcoming / Passed state (utils/eventStatus.js); a Google
  // Task's `passed` is its done tick, reminders keep their plain flag.
  const evDate = occDate || liveItem.date;
  const itemPassed = type === "event" && !fromGoogleTasks ? isEventPassed(liveItem, evDate, new Date()) : !!liveItem.passed;
  const TypeIcon = (type === "task" || fromGoogleTasks) ? CheckSquare : type === "event" ? CalendarDays : Bell;
  const typeLabel = (type === "task" || fromGoogleTasks) ? "Task" : type === "event" ? "Event" : "Reminder";

  const metaRow = (Icon, text) => (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 9, fontSize: 13, color: "var(--c-text-mid)", fontWeight: 600 }}>
      <span style={{ flexShrink: 0, marginTop: 1, color: "var(--c-text-soft)", display: "inline-flex" }}><Icon size={14} /></span>
      <span style={{ wordBreak: "break-word", lineHeight: 1.4 }}>{text}</span>
    </div>
  );

  return (
    <>
      {/* Backdrop — close only via X or outside click, not mark-done */}
      <div className="clarity-fade" onClick={() => { if (Date.now() - openedAtRef.current < 350) return; closeWeekBlockPop(); }}
        style={{ position: "fixed", inset: 0, zIndex: 3000, ...DETAIL_BACKDROP, display: confirmDelete ? "none" : undefined }} />

      {/* Centered detail panel — same style as month/year detail popup */}
      <div className="clarity-pop-c" onClick={e => e.stopPropagation()}
        style={{
          position: "fixed",
          top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 3001, width: "min(420px, 92vw)",
          background: "var(--c-bg-card)", borderRadius: 22,
          boxShadow: "0 14px 50px rgba(212,120,154,0.28)",
          fontFamily: "'Quicksand', sans-serif",
          padding: "16px 20px 22px",
          maxHeight: "85vh", overflowY: "auto",
          visibility: confirmDelete ? "hidden" : "visible",
        }}>

        {/* Top row: type label + close */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 18, fontWeight: 800, color: "var(--c-text)", letterSpacing: 0.3 }}>{typeLabel}</span>
            <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: 9, background: accentColor + "18", color: accentColor, flexShrink: 0 }}>
              <TypeIcon size={16} />
            </span>
          </div>
          <button onClick={closeWeekBlockPop}
            style={{ background: "var(--c-bg-soft)", border: "none", borderRadius: "50%", width: 30, height: 30, cursor: "pointer", color: "var(--c-text-soft)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><X size={15} /></button>
        </div>

        {/* Title */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 14 }}>
          {type === "event" && <div style={{ width: 10, height: 10, borderRadius: "50%", background: accentColor, flexShrink: 0, marginTop: 5 }} />}
          <span style={{ fontSize: 15, fontWeight: 700, color: "var(--c-text)", flex: 1, lineHeight: 1.4, wordBreak: "break-word",
            textDecoration: (taskIsDone || itemPassed) ? "line-through" : "none",
            opacity: (taskIsDone || itemPassed) ? 0.55 : 1,
          }}>{liveItem.title}</span>
          {type === "task" && liveItem.priority && <span style={{ background: (PRIORITY_COLORS[liveItem.priority] || "#d4789a") + "22", color: PRIORITY_COLORS[liveItem.priority] || "#d4789a", borderRadius: 8, padding: "3px 10px", fontSize: 11, fontWeight: 800, textTransform: "capitalize", flexShrink: 0, marginTop: 2 }}>{liveItem.priority}</span>}
        </div>

        {/* Actions — mark done/passed stays open; edit closes */}
        <ItemActionButtons type={fromGoogleTasks ? "task" : type} done={type === "task" ? taskIsDone : itemPassed}
          onToggleDone={() => {
            if (type === "task") toggleTaskDoneFromCal(liveItem.id);
            else if (type === "event" && !fromGoogleTasks) eventStatusTap(liveItem, evDate, { setCalEvents, openUpcoming: () => setUpcomingOpen(true) });
            else if (type === "event") setCalEvents(p => p.map(e => e.id === liveItem.id ? { ...e, passed: !e.passed } : e));
            else setCalReminders(p => p.map(r => r.id === liveItem.id ? { ...r, passed: !r.passed } : r));
          }}
          onEdit={handleEdit} onSkip={handleSkip} onDelete={() => (fromGoogleTasks ? setGTaskNotice(true) : setConfirmDelete(true))} />
        {gTaskNotice && <GoogleTaskDeleteNotice url={googleTaskUrl(liveItem)} onClose={() => setGTaskNotice(false)} />}

        {/* Meta rows */}
        <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
          {type === "task" && liveItem.startDate && metaRow(CalendarDays, `Start: ${liveItem.startDate}${liveItem.time ? " · " + liveItem.time + (liveItem.endTime ? " – " + liveItem.endTime : "") : ""}`)}
          {type === "task" && liveItem.dueDate && metaRow(Clock, `Due: ${liveItem.dueDate}${liveItem.dueTime ? ", " + liveItem.dueTime : ""}`)}
          {(type === "event" || type === "reminder") && (liveItem.date || liveItem.time) && metaRow(CalendarDays, `${liveItem.date || ""}${liveItem.time ? " · " + liveItem.time + (liveItem.endTime ? " – " + liveItem.endTime : "") : ""}${durationLabel(liveItem.time, liveItem.endTime) ? " (" + durationLabel(liveItem.time, liveItem.endTime) + ")" : ""}`)}
          {cat && metaRow(Tag, <><CategoryIcon cat={cat} size={12} /> {cat.title}</>)}
          {liveItem.location && metaRow(MapPin, liveItem.location)}
          {fromGoogleTasks && <GoogleTaskLink url={googleTaskUrl(liveItem)} />}
          {type === "task" && <ReminderToggleRow item={liveItem} onToggle={(on) => setTasks(p => p.map(t => t.id === liveItem.id ? setItemRemindersOn(t, on) : t))} />}
          {type === "event" && <ReminderToggleRow item={liveItem} onToggle={(on) => setCalEvents(p => p.map(e => e.id === liveItem.id ? setItemRemindersOn(e, on) : e))} />}
          {/* Sub-tasks sit ABOVE notes when present; notes float up on their own when there are none. */}
          {type === "task" && liveItem.subtasks?.length > 0 && (
            <div style={{ marginTop: 2 }}>
              <span style={{ fontSize: 13, fontWeight: 800, color: "var(--c-text-soft)", textTransform: "uppercase", letterSpacing: 0.8, display: "block", marginBottom: 6 }}>Sub-tasks</span>
              <div className="clarity-no-scrollbar" style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 160, overflowY: "auto", scrollbarWidth: "none", border: "1.5px solid #1a1a1a", borderRadius: 14, padding: 8 }}>
                {liveItem.subtasks.map(s => (
                  <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 9, padding: "8px 10px", borderRadius: 10, background: "var(--c-bg-soft)" }}>
                    <HabitCheckbox checked={!!s.done} color="#d4789a" size={20} burst={false} onChange={() => toggleSubtask(s.id)} />
                    <span style={{ flex: 1, fontSize: 13, color: "var(--c-text-mid)", fontWeight: 600, textDecoration: s.done ? "line-through" : "none", opacity: s.done ? 0.55 : 1, wordBreak: "break-word" }}>{s.title}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {(type === "task" || type === "event") && liveItem.embeds?.length > 0 && (
            <div style={{ marginTop: 2 }}>
              <span style={{ fontSize: 13, fontWeight: 800, color: "var(--c-text-soft)", textTransform: "uppercase", letterSpacing: 0.8, display: "block", marginBottom: 6 }}>Embedded Links</span>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                {liveItem.embeds.map(em => <EmbedPill key={em.id} url={em.url} description={em.description} />)}
              </div>
            </div>
          )}
          {liveItem.notes && (
            <div>
              <span style={{ fontSize: 13, fontWeight: 800, color: "var(--c-text-soft)", textTransform: "uppercase", letterSpacing: 0.8, display: "block", marginBottom: 6 }}>Notes</span>
              <div className="clarity-no-scrollbar clarity-notes-view" style={{ fontSize: 14, fontWeight: 600, color: "var(--c-text-mid)", background: "var(--c-bg-soft)", border: "1.5px solid #1a1a1a", borderRadius: 14, padding: "12px 16px", maxHeight: 120, overflowY: "auto", scrollbarWidth: "none", lineHeight: 1.5 }}
                onClick={handleNotesClick}
                dangerouslySetInnerHTML={{ __html: displayNotes }} />
            </div>
          )}
          {Array.isArray(liveItem.attachments) && liveItem.attachments.length > 0 && (
            <div style={{ marginTop: 2 }}>
              <span style={{ fontSize: 13, fontWeight: 800, color: "var(--c-text-soft)", textTransform: "uppercase", letterSpacing: 0.8, display: "block", marginBottom: 6 }}>Attachments</span>
              <AttachmentViewer items={liveItem.attachments} />
            </div>
          )}
        </div>
      </div>

      {/* Delete confirmation */}
      {confirmDelete && (
        <>
          {/* No pop-in on the task/event Delete panel (Jade, 2026-10-06). */}
          <div onClick={() => setConfirmDelete(false)}
            style={{ position: "fixed", inset: 0, zIndex: 3100, background: "rgba(60,30,50,0.45)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }} />
          <div onClick={e => e.stopPropagation()}
            style={{
              position: "fixed", top: "50%", left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 3101, width: "min(300px, 88vw)",
              background: "var(--c-bg-card)", borderRadius: 16,
              boxShadow: "0 8px 40px rgba(60,30,50,0.3)",
              padding: "24px 20px 20px",
              fontFamily: "'Quicksand', sans-serif",
              textAlign: "center",
            }}>
            <ConfirmHeader title={`Are you sure you want to delete this ${type}?`} />
            <div style={CONFIRM_STACK}>
              <button onClick={() => setConfirmDelete(false)} style={CONFIRM_CANCEL}>
                Cancel
              </button>
              <button onClick={handleDelete} style={CONFIRM_DANGER}>
                Delete
              </button>
            </div>
          </div>
        </>
      )}
      {upcomingOpen && (
        <MarkUpcomingPanel title={liveItem.title} date={evDate}
          weekStart={load("clarity_weekStart", "sunday")}
          onClose={() => setUpcomingOpen(false)}
          onKeep={() => { applyKeepUpcoming(liveItem, evDate, setCalEvents); setUpcomingOpen(false); }}
          onMove={(toDate) => {
            applyMoveEvent(liveItem, evDate, toDate, calEventOccurrenceOverrides, setCalEvents, setCalEventOccurrenceOverrides);
            setUpcomingOpen(false);
            closeWeekBlockPop();
          }} />
      )}
    </>
  );
}

// ─── REUSABLE MODAL COLOR PICKER ──────────────────────────────────────────────
// PICKER_PRESETS lives in ./constants/palettes (imported above).

// ModalColorPicker lives in ./components/pickers/ModalColorPicker.jsx (imported above).

// ─── SHARED ADD / EDIT PANEL ──────────────────────────────────────────────────
// One panel used for BOTH adding and editing tasks/events, across every planner
// view (week / month / year / list). Renders ONLY the card — each call site wraps
// it in its own backdrop/portal/Modal. The frequency picker + "Repeat Until" are
// shown for tasks only; events never see them. Reminders keep their own modal.
export function ItemPanel({
  mode,                 // "add" | "edit"
  initialType = "event",// starting type ("task" | "event")
  item = null,          // existing item when editing
  defaultDate,          // seed date for add mode
  defaultTime = "",     // seed time for add mode
  defaultTitle = "",    // seed title for add mode
  defaultCategoryId = "", // seed category for add mode (the quick-add bar's "More")
  defaultSubtasks = [],   // seed subtasks for add mode (the quick-add bar's "More")
  defaultDueDate = "",    // seed due date for add mode (the quick-add bar's "More")
  defaultEndTime = "",    // seed end time for add mode (the quick-add bar's "More")
  defaultDueTime = "",    // seed due time for add mode (the quick-add bar's "More")
  defaultAllDay = null,   // seed "All day" for add mode (the quick-add bar's "More"); null = the type's default
  defaultPriority = "",   // seed priority for add mode (the quick-add bar's "More")
  categories = [],
  setCategories = () => {},
  savedColors = [],
  addSavedColor = () => {},
  onSubmit,             // (type, payload) => void
  onClose = () => {},
}) {
  // Panel surface sits on a pastel theme-tinted background (see the container
  // style below), so inputs/buttons drop their border lines in favour of a
  // soft shadow to read as distinct white surfaces. Shadowed locally so the
  // sibling WeekBlockPopoverPortal component (which imports the same S) is unaffected.
  const S = {
    ..._S,
    input: { ..._S.input, border: "none", boxShadow: "0 1px 4px rgba(0,0,0,0.08)", fontSize: 14.5 },
    btn:   { ..._S.btn,   border: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" },
    label: { ..._S.label, color: "var(--c-text-accent)" },
  };
  const isEdit = mode === "edit";
  const [type, setType] = React.useState(initialType);
  // Brief pop + checkmark on Save before the panel actually closes, so the
  // panel doesn't just vanish the instant it's tapped.
  const [saving, setSaving] = React.useState(false);

  // Single DATE field for both types (mirrors the add panel). For tasks this maps
  // to startDate = dueDate; for events it's the event date.
  const seedDate = item ? (item.date || item.startDate || item.dueDate || defaultDate) : defaultDate;
  const [title, setTitle]     = React.useState(item?.title || defaultTitle || "");
  const [date, setDate]       = React.useState(seedDate || "");
  // Optional separate due date (tasks only). Empty unless the user set one that
  // differs from the start date — historically tasks stored startDate === dueDate.
  // A due date on the start day counts too when it was actually picked
  // (hasPickedDueDate). The quick-add bar only hands one over when it was picked.
  const [dueDate, setDueDate] = React.useState(isEdit ? (hasPickedDueDate(item) ? item.dueDate : "") : (defaultDueDate || ""));
  // The due date's own time (Jade, 2026-10-10): a deadline is one moment, so it
  // has no end time, and the start time doesn't apply to it. "" = no due time.
  const [dueTime, setDueTime] = React.useState(isEdit ? (item?.dueTime || "") : (defaultDueTime || ""));
  // A task saved before due times existed has no dueTime field. Unless one is
  // picked now, it's saved without one, so its reminders keep counting from the
  // start time as before (see taskReminderTime in taskLogic.js).
  const legacyNoDueTime = isEdit && !!item && (item.dueTime === undefined || item.dueTime === null);
  const [hasTime, setHasTime] = React.useState(isEdit ? !!item?.time : !!defaultTime);
  // "All day" switch (Jade, 2026-10-10). An event with no time is all day; a
  // task only when it was saved with the switch on. A new item follows its
  // type (events on, tasks off) until the switch is touched; a time already
  // given (a tapped week slot) means off.
  const [allDayPick, setAllDayPick] = React.useState(
    isEdit ? (item?.time ? false : (initialType === "event" ? true : !!item?.allDay)) : defaultAllDay
  );
  const allDay = allDayPick ?? (type === "event" && !hasTime);
  // Reminders are their OWN section now, not a sub-part of the time section:
  // an item with no clock time can still want "2 days before". Opens itself
  // when editing something that already has reminders set.
  const [hasReminder, setHasReminder] = React.useState(isEdit ? !!(item?.reminders?.length) : false);
  const [time, setTime]       = React.useState(item?.time || defaultTime || "");
  const [endTime, setEndTime] = React.useState(item?.endTime || defaultEndTime || (defaultTime ? `${String(parseInt(defaultTime)+1).padStart(2,"00")}:00` : ""));
  const [categoryId, setCategoryId] = React.useState(item?.goalId || item?.categoryId || defaultCategoryId || "");
  const [showNewCat, setShowNewCat] = React.useState(false); // inline "+ New category" expansion
  const [notes, setNotes]     = React.useState(item?.notes || "");
  const [subtasks, setSubtasks] = React.useState(item?.subtasks || defaultSubtasks);
  const [newSubtask, setNewSubtask] = React.useState("");
  const [embeds, setEmbeds] = React.useState(item?.embeds || []);
  const [newEmbedUrl, setNewEmbedUrl] = React.useState("");
  const [newEmbedDesc, setNewEmbedDesc] = React.useState("");
  const [newEmbedError, setNewEmbedError] = React.useState("");
  const [location, setLocation] = React.useState(item?.location || "");
  const [reminders, setReminders] = React.useState(item?.reminders || []);
  // Which date a task's reminders count down from, when start and due dates
  // differ — only meaningful then, so it's only saved in that case (see
  // handleSave). Defaults to "due" for a task that already has both dates set
  // but hasn't chosen yet, since "remind me before it's due" is what most
  // people mean; carries over the existing choice on edit.
  const [remindAnchor, setRemindAnchor] = React.useState(item?.remindAnchor || "due");
  const [reminderType, setReminderType] = React.useState(item?.reminderType || "notification");
  const [alarmSound, setAlarmSound] = React.useState(item?.alarmSound || DEFAULT_ALARM_SOUND);
  const [alarmSoundUri, setAlarmSoundUri] = React.useState(item?.alarmSoundUri || "");
  const [alarmSoundLabel, setAlarmSoundLabel] = React.useState(item?.alarmSoundLabel || "");
  const [attachments, setAttachments] = React.useState(item?.attachments || []);
  const [color, setColor]     = React.useState(() => {
    const raw = item?.color || "";
    if (!raw) return "";
    // Every task/event used to get a fixed default color baked in even when the
    // user never opened the color picker, which permanently hid its category
    // color. If this item's stored color still matches that stale baked-in
    // default and it has a category, treat it as "no color chosen" so editing
    // it lets the category color take back over instead of re-saving the default.
    const def = initialType === "event" ? DEFAULT_EVENT_COLOR : DEFAULT_TASK_COLOR;
    const hasCategory = !!(item?.categoryId || item?.goalId);
    return (raw === def && hasCategory) ? "" : raw;
  });
  const [priority, setPriority] = React.useState(item?.priority || defaultPriority || "");
  // Opt-out: true unless explicitly turned off, so existing tasks keep showing
  // in the Clock-In tab's today's plan without the user doing anything.
  const [addToClockIn, setAddToClockIn] = React.useState(item?.addToClockIn !== false);

  // Frequency (tasks & events)
  const [freqType, setFreqType]   = React.useState(item?.frequencyType || "once"); // once|daily|specific|monthly|yearly|interval
  // Interval: every N days / weeks / months from the start date (same picker as habits).
  const [interval, setInterval_] = React.useState({ intervalUnit: item?.intervalUnit, intervalEvery: item?.intervalEvery });
  const [freqDays, setFreqDays]   = React.useState(item?.frequencyDays || []);
  const [monthDates, setMonthDates] = React.useState(item?.monthDates || []);
  // Yearly = specific calendar dates that recur every year (e.g. birthdays).
  // Stored as `${month}-${day}` strings (1-based, no zero-pad), mirroring habits.
  const [yearDates, setYearDates] = React.useState(item?.yearDates || []);
  const [yearPickMonth, setYearPickMonth] = React.useState(new Date().getMonth()); // 0-based, picker-local
  // Yearly recurrence scope: "annual" repeats the picked dates every year,
  // "thisYear" assigns them only in the start date's year (no future repeats).
  const [yearlyScope, setYearlyScope] = React.useState(item?.yearlyOnce ? "thisYear" : "annual");
  const [repeatEnd, setRepeatEnd] = React.useState(item?.repeatEnd || "");

  // A recurring task's due date input is disabled — drop any leftover value
  // so a stale date never sneaks into the saved payload.
  React.useEffect(() => {
    if (freqType !== "once" && dueDate) { setDueDate(""); setDueTime(""); }
  }, [freqType]); // eslint-disable-line react-hooks/exhaustive-deps

  const canSave = title.trim();

  // The clock time reminders count back from — same rule as taskReminderTime
  // (taskLogic.js): "Remind before: Due date" uses the due time, else the start
  // time. With none, only day/week offsets make sense (they fall back to 9:00
  // AM), so sub-day ones are dropped, as the time row's Remove already does.
  const anchorsOnDue = type === "task" && !!dueDate && freqType === "once" && remindAnchor === "due";
  const reminderHasTime = anchorsOnDue
    ? !!(dueTime || (legacyNoDueTime && hasTime && time))
    : hasTime;
  React.useEffect(() => {
    if (!reminderHasTime) setReminders(rs => {
      const kept = (rs || []).filter(m => m >= 1440);
      return kept.length === (rs || []).length ? rs : kept;
    });
  }, [reminderHasTime]);

  const addEmbed = () => {
    if (!isValidHttpUrl(newEmbedUrl)) { setNewEmbedError("Enter a valid link (https://…)"); return; }
    setEmbeds(p => [...p, { id: uid(), url: newEmbedUrl.trim(), description: newEmbedDesc.trim() }]);
    setNewEmbedUrl(""); setNewEmbedDesc(""); setNewEmbedError("");
  };

  // Builds the saved item from the form, with `titleText` as its title (the
  // quick-add paths below build several items from one form).
  const buildPayload = (titleText) => buildItemPayload({
    isEdit, item, initialType, type, title: titleText, date, dueDate, hasTime, time, endTime, allDay,
    dueTime: legacyNoDueTime && !dueTime ? undefined : dueTime,
    categoryId, color, notes, subtasks, embeds, location, reminders, remindAnchor, reminderType,
    alarmSound, alarmSoundUri, alarmSoundLabel, attachments, priority, addToClockIn,
    freqType, freqDays, monthDates, yearDates, yearlyScope, repeatEnd,
    intervalUnit: interval.intervalUnit, intervalEvery: interval.intervalEvery,
  });

  const commitSave = () => {
    if (!canSave) return;
    if (color) addSavedColor(color);
    onSubmit(type, buildPayload(title.trim()), initialType);
  };

  const handleSave = () => {
    if (!canSave || saving) return;
    setSaving(true);
    setTimeout(commitSave, 380);
  };

  // ── Inline Start / Due sections ─────────────────────────────────────────
  const [openDateSec, setOpenDateSec] = React.useState(null); // "start" | "due" | null — one at a time
  const weekStart = load("clarity_weekStart", "sunday");
  const pickStart = (d) => {
    setDate(d);
    if (dueDate && dueDate < d) { setDueDate(""); setDueTime(""); } // a due date can't come before the start
  };
  const setStartTime = (v) => {
    if (v) {
      setHasTime(true); setTime(v);
      if (endTime && endTime <= v) setEndTime("");
      return;
    }
    setHasTime(false); setTime(""); setEndTime("");
    // Day/week reminders survive losing the time (they fall back to 9:00 AM —
    // see UNTIMED_REMINDER_HOUR); sub-day offsets have nothing left to count
    // back from, so they go.
    setReminders(rs => (rs || []).filter(m => m >= 1440));
  };
  // Summaries are [date, time] pairs, drawn as a bold date with the time as a
  // soft pill beside it (Jade, 2026-10-11: "Thu, Oct 15 · 6:00 PM – 7:00 PM" on
  // one line read cluttered). A plain string ("None", "—") is drawn as is.
  const startSummary = [
    date ? quickDateLabel(date) : "No date",
    hasTime && time ? `${fmtQuickTime(time)}${endTime ? ` – ${fmtQuickTime(endTime)}` : ""}` : (allDay ? "All day" : ""),
  ];
  const dueSummary = freqType !== "once" ? "-"
    : dueDate ? [quickDateLabel(dueDate), dueTime ? fmtQuickTime(dueTime) : ""]
    : "None";
  // Collapsed: the label above a white row with the summary + chevron. Open:
  // the same card grows to hold the calendar and the time rows.
  const dateSection = (key, label, summary, body, disabled = false) => {
    const open = !disabled && openDateSec === key;
    return (
      <div>
        <p style={{ ...S.label, opacity: disabled ? 0.5 : 1 }}>{label}</p>
        <div style={{ background: "var(--c-bg-card)", borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", opacity: disabled ? 0.5 : 1 }}>
          <button type="button" disabled={disabled} aria-expanded={open}
            onClick={() => setOpenDateSec(o => (o === key ? null : key))}
            style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "11px 12px 11px 14px", border: "none", background: "transparent",
              cursor: disabled ? "not-allowed" : "pointer", fontFamily: "inherit", textAlign: "left" }}>
            {Array.isArray(summary) ? (
              <span style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 8, overflow: "hidden" }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: "var(--c-text)", whiteSpace: "nowrap", flexShrink: 0 }}>{summary[0]}</span>
                {summary[1] && (
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: "var(--c-text-accent)", background: "var(--c-bg-active)", borderRadius: 999, padding: "3px 9px",
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0, fontVariantNumeric: "tabular-nums" }}>{summary[1]}</span>
                )}
              </span>
            ) : (
              <span style={{ flex: 1, minWidth: 0, fontSize: 14.5, fontWeight: 600, color: "var(--c-text-soft)",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{summary}</span>
            )}
            <ChevronDown size={16} style={{ flexShrink: 0, color: "#a07090", transition: "transform 0.2s", transform: open ? "rotate(180deg)" : "none" }} />
          </button>
          {/* Pulled up under the date line, no gap before the month row (Jade). */}
          {open && <div style={{ padding: "0 6px 12px", marginTop: -8 }}>{body}</div>}
        </div>
      </div>
    );
  };

  return (
    <div onClick={e => e.stopPropagation()} style={{
      background: "var(--c-bg-active)", borderRadius: 20,
      width: "min(640px,95vw)", maxHeight: "90vh", overflowY: "auto",
      WebkitOverflowScrolling: "touch", overscrollBehavior: "contain",
      boxShadow: "0 8px 40px rgba(212,120,154,0.28)",
      padding: "24px 28px 28px",
      fontFamily: "'Quicksand','Nunito',sans-serif",
      // Promote this scroller to its own GPU layer so touch-scrolling the form
      // stays smooth. (The smooth Add panel proves this helps — removing it made
      // Add janky too, so it is kept for both Add and Edit.)
      transform: "translateZ(0)", willChange: "transform",
      contain: "paint",
    }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <span style={{ fontSize: 16, fontWeight: 700, color: "var(--c-text-soft)" }}>
          {isEdit ? (type === "task" ? "Edit Task" : "Edit Event") : "New Item"}
        </span>
        <button onClick={onClose} style={{ border: "none", background: "transparent", fontSize: 22, color: "var(--c-text-soft)", cursor: "pointer", lineHeight: 1, padding: 0 }}><X size={16} /></button>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {/* Title row */}
        <input
          // No autoFocus: opening the panel shouldn't pop the keyboard up by itself.
          // A task/event title is never a saved credential/address, but the
          // browser offers its autofill strip (passwords/cards/addresses)
          // above the keyboard for any plain text field — which lands
          // between this panel and the keyboard and reads as a gap.
          autoComplete="off"
          placeholder="Add Title"
          value={title}
          onChange={e => setTitle(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); handleSave(); } }}
          style={{
            width: "100%", boxSizing: "border-box",
            border: "none", borderBottom: "2px solid var(--c-border-mid)",
            background: "transparent", outline: "none",
            fontSize: 20, fontWeight: 600, color: "var(--c-text)",
            padding: "4px 0 10px", fontFamily: "inherit",
          }}
        />

        {/* Type toggle: Task / Event — switchable in both add and edit mode */}
        <div style={{ display: "flex", gap: 10 }}>
          {/* Same colours for both (Jade, 2026-10-10): the app accent, not the
              old yellow-for-task / violet-for-event. */}
          {[["task", "Set as Task"], ["event", "Set as Event"]].map(([key, label]) => {
            const checked = type === key;
            const c = "var(--c-primary)";
            const bg = "var(--c-bg-active)";
            return (
              <button key={key} onClick={() => setType(key)}
                style={{ flex: 1, display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", borderRadius: 12, border: "none", boxShadow: checked ? `0 0 0 2px ${c}, 0 1px 4px rgba(0,0,0,0.07)` : "0 1px 4px rgba(0,0,0,0.07)", background: checked ? bg : "var(--c-bg-card)", cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s" }}>
                <div style={{ width: 18, height: 18, borderRadius: 5, border: `2px solid ${checked ? c : "var(--c-border)"}`, background: checked ? c : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {checked && <span style={{ color: "var(--c-on-primary)", fontSize: 11, fontWeight: 900 }}>✓</span>}
                </div>
                <span style={{ fontSize: 14.5, fontWeight: 700, color: checked ? "var(--c-text-accent)" : "var(--c-text-soft)" }}>{label}</span>
              </button>
            );
          })}
        </div>

        {/* Start / Due — inline expanding sections (Jade, 2026-10-10). Each
            date carries its own time: Start = start time + optional end time,
            Due = due time only. One open at a time; picks apply at once (the
            form has its own Save). Calendar + time rows are shared with the
            quick-add date panel (DatePickParts.jsx). Events get one DATE
            section. The due section is disabled for recurring tasks: a
            recurring series doesn't really have a single due date, "REPEAT
            UNTIL" (in the Frequency section below) is the equivalent for it. */}
        {dateSection("start", type === "task" ? "START" : "DATE", startSummary, (<>
          <MonthPicker start={date} due={type === "task" ? dueDate : ""} isTask={type === "task"} weekStart={weekStart} onPick={pickStart} />
          <div style={{ marginTop: 12, paddingTop: 6, borderTop: "1px solid var(--c-border)" }}>
            <AllDayRow on={allDay} onChange={on => { setAllDayPick(on); if (on) setStartTime(""); }} />
            {!allDay && <TimeRow label={type === "task" ? "Start time" : "Time"} value={hasTime ? time : ""} onChange={setStartTime} />}
            {!allDay && hasTime && time && <EndTimeRow start={time} value={endTime} onChange={setEndTime} />}
          </div>
        </>))}

        {type === "task" && dateSection("due", <>DUE <span style={{ fontWeight: 600, letterSpacing: 0, textTransform: "none" }}>(optional)</span></>, dueSummary, (<>
          <MonthPicker start={date} due={dueDate} isTask editingDue weekStart={weekStart} onPick={d => { if (date && d < date) setDate(d); setDueDate(d); }} />
          <div style={{ marginTop: 12, paddingTop: 6, borderTop: "1px solid var(--c-border)" }}>
            <TimeRow label="Due time" value={dueTime} onChange={v => { setDueTime(v); if (v && !dueDate) setDueDate(date); }} />
          </div>
          {dueDate && (
            <button type="button" onClick={() => { setDueDate(""); setDueTime(""); setOpenDateSec(null); }}
              style={{ marginTop: 6, background: "none", border: "none", cursor: "pointer", color: "var(--c-text-soft)", fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", gap: 4, padding: "4px 0", fontFamily: "inherit" }}>
              <X size={13} /> Clear due date
            </button>
          )}
        </>), freqType !== "once")}
        {type === "task" && freqType !== "once" && (
          <p style={{ margin: "-6px 0 0", fontSize: 10, color: "#000000", fontStyle: "italic" }}>Recurring tasks use "Repeat until" below instead of a due date</p>
        )}

        {/* Reminders — tasks & events. Scheduled delivery needs the native
            AlarmScheduler/NotificationScheduler plugins (see
            src/capacitor/alarmSync.js, notificationSync.js) — no web
            equivalent, so this stays hidden on web/PWA builds. Reminder
            offsets are minutes-*before-time*, so they're meaningless (and
            hidden) until the user actually sets a time. */}
        {(type === "task" || type === "event") && Capacitor.isNativePlatform() && (
          !hasReminder ? (
            <div>
              <button
                onClick={() => setHasReminder(true)}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  background: "var(--c-bg-card)", border: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                  borderRadius: 10, padding: "8px 14px",
                  fontSize: 12, fontWeight: 600, color: "var(--c-text-soft)",
                  cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s",
                }}
                onMouseEnter={e => { e.currentTarget.style.color = "#d4789a"; }}
                onMouseLeave={e => { e.currentTarget.style.color = "var(--c-text-soft)"; }}
              >
                <Bell size={13} /> + Set reminder
              </button>
            </div>
          ) : (
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <p style={{ ...S.label, margin: 0 }}>REMINDER</p>
              <button
                onClick={() => { setHasReminder(false); setReminders([]); }}
                style={{ background: "none", border: "none", cursor: "pointer", color: "var(--c-text-soft)", fontSize: 11, fontWeight: 600, display: "flex", alignItems: "center", gap: 3, padding: 0, fontFamily: "inherit" }}
                onMouseEnter={e => e.currentTarget.style.color = "#e8647a"}
                onMouseLeave={e => e.currentTarget.style.color = "#c0a0b8"}
              >
                <X size={12} /> Remove
              </button>
            </div>
            {/* Only shown when start and due dates actually differ — with a
                single date there's nothing to choose between. */}
            {type === "task" && dueDate && freqType === "once" && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 11, color: "var(--c-text-soft)", fontWeight: 600 }}>Remind before:</span>
                <span style={{ display: "inline-flex", borderRadius: 999, overflow: "hidden", border: "1px solid var(--c-border)" }}>
                  <button type="button" onClick={() => setRemindAnchor("start")}
                    style={{ padding: "5px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer", border: "none", color: remindAnchor === "start" ? "#fff" : "var(--c-text-mid)", background: remindAnchor === "start" ? "linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))" : "var(--c-bg-card)" }}>
                    Start date
                  </button>
                  <button type="button" onClick={() => setRemindAnchor("due")}
                    style={{ padding: "5px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer", border: "none", color: remindAnchor === "due" ? "#fff" : "var(--c-text-mid)", background: remindAnchor === "due" ? "linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))" : "var(--c-bg-card)" }}>
                    Due date
                  </button>
                </span>
              </div>
            )}
            {/* No time set ⇒ day/week offsets only; the full preset row
                (at start / 5 / 10 / 15 / 30 min / 1 hr + custom) appears on
                its own the moment a time is set above. */}
            <ReminderPicker value={reminders} onChange={setReminders} onlyDayWeek={!reminderHasTime}
              alertType={reminderType} onAlertTypeChange={setReminderType}
              alarmSound={alarmSound} onAlarmSoundChange={setAlarmSound}
              alarmSoundUri={alarmSoundUri} alarmSoundLabel={alarmSoundLabel}
              onCustomSoundChange={(picked) => { setAlarmSoundUri(picked?.uri || ""); setAlarmSoundLabel(picked?.label || ""); }} />
          </div>
          )
        )}

        {/* Location — tasks & events (OpenStreetMap place suggestions) */}
        {(type === "task" || type === "event") && (
          <div>
            <p style={S.label}>LOCATION / PLACE</p>
            <LocationAutocomplete value={location} onChange={setLocation} inputStyle={S.input} />
          </div>
        )}

        {/* Attachments — events & tasks */}
        {(type === "event" || type === "task") && (
          <div>
            <p style={S.label}>ATTACHMENTS</p>
            <AttachmentField value={attachments} onChange={setAttachments} />
          </div>
        )}

        {/* Category */}
        <div>
          <p style={S.label}>CATEGORY</p>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <button onClick={() => setCategoryId("")}
              style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 14.5, fontWeight: 700, padding: "6px 13px", borderRadius: 20, border: "none", boxShadow: !categoryId ? "0 0 0 1.5px var(--c-primary), 0 1px 3px rgba(0,0,0,0.06)" : "0 1px 3px rgba(0,0,0,0.06)", background: "var(--c-bg-card)", color: !categoryId ? "var(--c-primary)" : "#a07090", cursor: "pointer", fontFamily: "inherit" }}>
              <XCircle size={12} />None
            </button>
            {categories.map(c => {
              const selected = categoryId === c.id;
              // vividDark() rebuilds the category's hue at a fixed rich/dark lightness
              // instead of using its raw picked color — a pale category color (light
              // pink, light yellow, ...) used as-is read as washed-out, low-contrast
              // text/icon on the selected chip's light tinted background.
              // Every pill in its own category colour, like the habit form (Jade,
              // 2026-10-17; unselected ones were a fixed mauve). categoryInk: rich in
              // light mode, a light pastel tint in dark mode.
              const selectedColor = categoryInk(c.color);
              return (
                <button key={c.id} onClick={() => setCategoryId(c.id)}
                  style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 14.5, fontWeight: 700, padding: "6px 13px", borderRadius: 20, border: "none", boxShadow: selected ? `0 0 0 1.5px ${selectedColor}, 0 1px 3px rgba(0,0,0,0.06)` : "0 1px 3px rgba(0,0,0,0.06)", background: selected ? c.color + "22" : "var(--c-bg-card)", color: selectedColor, cursor: "pointer", fontFamily: "inherit" }}>
                  <CategoryIcon cat={c} size={15} color={selectedColor} /> {c.title}
                </button>
              );
            })}
            {!showNewCat && (
              <button onClick={() => setShowNewCat(true)}
                style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 14.5, fontWeight: 700, padding: "6px 13px", borderRadius: 20, border: "1.5px dashed var(--c-border-mid)", background: "transparent", color: "var(--c-text-soft)", cursor: "pointer", fontFamily: "inherit" }}>
                <Plus size={14} /> New category
              </button>
            )}
          </div>
          {showNewCat && (
            <InlineCategoryCreator
              categories={categories}
              setCategories={setCategories}
              savedColors={savedColors}
              addSavedColor={addSavedColor}
              S={S}
              onCreated={newCat => { setCategoryId(newCat.id); setShowNewCat(false); }}
              onCancel={() => setShowNewCat(false)}
            />
          )}
        </div>

        {/* Priority — tasks only. Flag picker: None + Low/Medium/High/Urgent,
            colour-coded so the level reads at a glance (Todoist/ClickUp-style). */}
        {type === "task" && (
          <div>
            <p style={S.label}>PRIORITY</p>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <button onClick={() => setPriority("")}
                style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 14.5, fontWeight: 700, padding: "6px 13px", borderRadius: 20, border: "none", boxShadow: !priority ? "0 0 0 1.5px #a07090, 0 1px 3px rgba(0,0,0,0.06)" : "0 1px 3px rgba(0,0,0,0.06)", background: "var(--c-bg-soft)", color: !priority ? "var(--c-text)" : "#a07090", cursor: "pointer", fontFamily: "inherit" }}>
                <Flag size={14} /> None
              </button>
              {TASK_PRIORITIES.map(({ id, label }) => {
                const c = PRIORITY_COLORS[id];
                const active = priority === id;
                return (
                  <button key={id} onClick={() => setPriority(id)}
                    style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 14.5, fontWeight: 700, padding: "6px 13px", borderRadius: 20, border: "none", boxShadow: active ? `0 0 0 1.5px ${c}, 0 1px 3px rgba(0,0,0,0.06)` : "0 1px 3px rgba(0,0,0,0.06)", background: active ? c + "22" : "var(--c-bg-card)", color: active ? c : "#a07090", cursor: "pointer", fontFamily: "inherit" }}>
                    <Flag size={14} fill={active ? c : "none"} /> {label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Add to Clock-In — opt-out: on by default so nothing existing
            disappears. Quick tasks don't need to sit in the focus queue. */}
        {type === "task" && (
          <div onClick={() => setAddToClockIn(v => !v)}
            style={{ display: "flex", alignItems: "center", gap: 9, padding: "9px 12px", background: "var(--c-bg-card)", border: "none", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", borderRadius: 12, cursor: "pointer" }}>
            <HabitCheckbox checked={addToClockIn} color="var(--c-primary)" size={18} burst={false}
              onChange={() => setAddToClockIn(v => !v)} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: "var(--c-text)" }}>Add to Clock-In</div>
            </div>
          </div>
        )}

        {/* Frequency — tasks and events */}
        {(type === "task" || type === "event") && (
          <div>
            <p style={S.label}>FREQUENCY</p>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {[
                { val: "once",     label: "Once"    },
                { val: "daily",    label: "Daily"   },
                { val: "specific", label: "Weekly"  },
                { val: "monthly",  label: "Monthly"  },
                { val: "yearly",   label: "Yearly"  },
                { val: "interval", label: "Interval" },
              ].map(({ val, label }) => {
                const active = freqType === val;
                return (
                  <button key={val} onClick={() => setFreqType(val)}
                    style={{ ...S.btn, flex: "1 1 88px", minHeight: 0, padding: "9px 10px", fontSize: 14.5, fontWeight: active ? 700 : 500, background: active ? "var(--c-bg-card)" : "transparent", color: active ? "var(--c-primary)" : "#a07090", borderColor: active ? "var(--c-primary)" : undefined }}>
                    {label}
                  </button>
                );
              })}
            </div>

            {freqType === "interval" && (
              <IntervalPicker value={interval} anchor={date} btnStyle={S.btn} background="var(--c-bg-soft)"
                onChange={setInterval_} />
            )}

            {freqType === "specific" && (
              <div style={{ marginTop: 8, background: "var(--c-bg-soft)", borderRadius: 10, padding: "10px 12px", border: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                <p style={{ fontSize: 11, color: "#000000", margin: "0 0 8px" }}>Which days?</p>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
                  {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d, i) => (
                    <button key={d} onClick={() => setFreqDays(p => p.includes(i) ? p.filter(x => x !== i) : [...p, i])}
                      style={{ ...S.btn, fontSize: 10, padding: "7px 2px", minWidth: 0, textAlign: "center", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "clip", fontWeight: freqDays.includes(i) ? 800 : 400, background: freqDays.includes(i) ? "var(--c-bg-active)" : "transparent", color: freqDays.includes(i) ? "var(--c-primary)" : "#a07090", borderColor: freqDays.includes(i) ? "var(--c-primary)" : undefined }}>
                      {d}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {freqType === "monthly" && (
              <div style={{ marginTop: 8, background: "var(--c-bg-soft)", borderRadius: 10, padding: "10px 12px", border: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                <p style={{ fontSize: 11, color: "#000000", margin: "0 0 8px" }}>Pick dates of the month:</p>
                <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                  {Array.from({ length: 31 }, (_, i) => (
                    <button key={i+1} onClick={() => setMonthDates(p => p.includes(i+1) ? p.filter(x => x !== i+1) : [...p, i+1])}
                      style={{ ...S.btn, fontSize: 11, width: 32, height: 32, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", background: monthDates.includes(i+1) ? "var(--c-bg-active)" : "transparent", color: monthDates.includes(i+1) ? "var(--c-primary)" : "#a07090", borderColor: monthDates.includes(i+1) ? "var(--c-primary)" : undefined }}>
                      {i+1}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {freqType === "yearly" && (
              <div style={{ marginTop: 8, background: "var(--c-bg-soft)", borderRadius: 10, padding: "10px 12px", border: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                {/* Month selector */}
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 8 }}>
                  {MONTHS_SHORT.map((m, mi) => (
                    <button key={m} onClick={() => setYearPickMonth(mi)}
                      style={{ ...S.btn, fontSize: 12, padding: "5px 9px", minHeight: 0, background: yearPickMonth === mi ? "var(--c-bg-active)" : "transparent", color: yearPickMonth === mi ? "var(--c-primary)" : "#a07090", borderColor: yearPickMonth === mi ? "var(--c-primary)" : undefined }}>
                      {m}
                    </button>
                  ))}
                </div>
                {/* Day grid for the selected month */}
                <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                  {Array.from({ length: DAYS_IN_MONTH[yearPickMonth] }, (_, i) => {
                    const key = `${yearPickMonth + 1}-${i + 1}`;
                    const on = yearDates.includes(key);
                    return (
                      <button key={key} onClick={() => setYearDates(p => p.includes(key) ? p.filter(x => x !== key) : [...p, key])}
                        style={{ ...S.btn, fontSize: 11, width: 32, height: 32, padding: 0, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center", background: on ? "var(--c-bg-active)" : "transparent", color: on ? "var(--c-primary)" : "#a07090", borderColor: on ? "var(--c-primary)" : undefined }}>
                        {i + 1}
                      </button>
                    );
                  })}
                </div>
                {yearDates.length > 0 && (
                  <p style={{ fontSize: 11, color: "var(--c-text-mid)", margin: "8px 0 0", fontWeight: 700 }}>
                    {[...yearDates].sort((a, b) => { const [am, ad] = a.split("-").map(Number); const [bm, bd] = b.split("-").map(Number); return am - bm || ad - bd; })
                      .map(k => { const [mo, dy] = k.split("-").map(Number); return `${MONTHS_SHORT[mo - 1]} ${dy}`; }).join(" · ")}
                  </p>
                )}
              </div>
            )}

            {/* Yearly recurrence scope — replaces the repeat-until date for yearly */}
            {freqType === "yearly" && (
              <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 8 }}>
                {[
                  { val: "annual",   label: "Repeats every year" },
                  { val: "thisYear", label: "This year only" },
                ].map(opt => {
                  const active = yearlyScope === opt.val;
                  return (
                    <button key={opt.val} onClick={() => setYearlyScope(opt.val)}
                      style={{ display: "flex", alignItems: "flex-start", gap: 9, textAlign: "left", padding: "9px 12px", borderRadius: 10, border: "none", boxShadow: active ? "0 0 0 1.5px var(--c-primary), 0 1px 3px rgba(0,0,0,0.06)" : "0 1px 3px rgba(0,0,0,0.06)", background: "var(--c-bg-card)", cursor: "pointer", fontFamily: "inherit" }}>
                      <div style={{ width: 18, height: 18, borderRadius: 5, flexShrink: 0, marginTop: 1, border: `2px solid ${active ? "var(--c-primary)" : "#d0b8c8"}`, background: active ? "var(--c-primary)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {active && <span style={{ color: "#fff", fontSize: 11, fontWeight: 900 }}>✓</span>}
                      </div>
                      <div style={{ fontSize: 14.5, fontWeight: 700, color: active ? "var(--c-primary)" : "var(--c-text)" }}>{opt.label}</div>
                    </button>
                  );
                })}
              </div>
            )}

            {freqType !== "once" && freqType !== "yearly" && (
              <div style={{ marginTop: 8 }}>
                <p style={{ ...S.label, marginBottom: 4 }}>REPEAT UNTIL</p>
                <NativePickerInput type="date" value={repeatEnd} onChange={e => setRepeatEnd(e.target.value)} style={S.input} min={date} />
                <p style={{ margin: "4px 0 0", fontSize: 10, color: "#000000", fontStyle: "italic" }}>Leave blank to repeat indefinitely</p>
              </div>
            )}
          </div>
        )}

        {/* Notes */}
        <div>
          <p style={S.label}>NOTES</p>
          <RichNotesEditor value={notes} onChange={setNotes} placeholder="Add notes..." />
        </div>

        {/* Embedded Links — tasks & events, kept separate from Notes. Pasted
            links show as a small site-name pill (e.g. "canva.com") with an
            optional description caption underneath, instead of a raw URL. */}
        {(type === "task" || type === "event") && (
          <div>
            <p style={S.label}>EMBEDDED LINKS</p>
            {embeds.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 10 }}>
                {embeds.map(em => (
                  <EmbedPill key={em.id} url={em.url} description={em.description}
                    onRemove={() => setEmbeds(p => p.filter(x => x.id !== em.id))} />
                ))}
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <input
                placeholder="Paste a link..."
                value={newEmbedUrl}
                onChange={e => { setNewEmbedUrl(e.target.value); setNewEmbedError(""); }}
                style={{ ...S.input, fontWeight: 600 }}
              />
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  placeholder="Description (optional)..."
                  value={newEmbedDesc}
                  onChange={e => setNewEmbedDesc(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") addEmbed(); }}
                  style={{ ...S.input, flex: 1, fontWeight: 600 }}
                />
                <button onClick={addEmbed} style={S.addBtn}>
                  <Plus size={13} style={{ marginRight: 4 }} />Add
                </button>
              </div>
              {newEmbedError && <span style={{ fontSize: 11, color: "#e8647a" }}>{newEmbedError}</span>}
            </div>
          </div>
        )}

        {/* Sub-tasks — tasks only */}
        {type === "task" && (
          <div>
            <p style={S.label}>SUB-TASKS</p>
            {subtasks.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 8 }}>
                {subtasks.map(s => (
                  <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", borderRadius: 12, border: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.06)", background: "var(--c-bg-soft)" }}>
                    <HabitCheckbox
                      checked={!!s.done}
                      color="var(--c-primary)"
                      size={22}
                      burst={false}
                      onChange={() => setSubtasks(p => p.map(x => x.id === s.id ? { ...x, done: !x.done } : x))}
                    />
                    <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: "var(--c-text)", textDecoration: s.done ? "line-through" : "none", opacity: s.done ? 0.55 : 1 }}>{s.title}</span>
                    <button onClick={() => setSubtasks(p => p.filter(x => x.id !== s.id))}
                      style={{ background: "none", border: "none", cursor: "pointer", color: "var(--c-text-soft)", padding: 0, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, alignSelf: "center" }}>
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <input
                placeholder="Add a sub-task..."
                value={newSubtask}
                onChange={e => setNewSubtask(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && newSubtask.trim()) { setSubtasks(p => [...p, { id: uid(), title: newSubtask.trim(), done: false }]); setNewSubtask(""); } }}
                style={{ ...S.input, flex: 1, fontWeight: 600 }}
              />
              <button
                onClick={() => { if (newSubtask.trim()) { setSubtasks(p => [...p, { id: uid(), title: newSubtask.trim(), done: false }]); setNewSubtask(""); } }}
                style={S.addBtn}>
                <Plus size={13} style={{ marginRight: 4 }} />Add
              </button>
            </div>
          </div>
        )}

        {/* Color */}
        <div>
          <p style={S.label}>COLOR</p>
          <ModalColorPicker currentColor={color} onColorChange={setColor} onSaveColor={addSavedColor} savedColors={savedColors} S={S} />
        </div>

        {/* Save button */}
        <button onClick={handleSave} disabled={!canSave || saving}
          style={{ ...S.addBtn, width: "100%", fontSize: 15, padding: "13px 0", borderRadius: 14, opacity: canSave ? 1 : 0.45, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, boxShadow: canSave ? "0 4px 16px rgba(212,88,154,0.3)" : "none", marginTop: 4 }}>
          {saving
            ? <><CheckCircle size={16} />Saved!</>
            : (isEdit ? "Save Changes" : `Save ${type === "task" ? "Task" : "Event"}`)}
        </button>
      </div>
    </div>
  );
}
